import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Users } from 'lucide-react';
import { GuardianApi, setGuardianToken } from '@/lib/guardian-api';

const schema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});
type Form = z.infer<typeof schema>;

export function GuardianLoginPage() {
  const navigate = useNavigate();
  const [err, setErr] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: Form) => {
    setErr(null);
    try {
      const session = await GuardianApi.login(values.username.trim(), values.password);
      setGuardianToken(session.tokens.accessToken);
      navigate('/guardian');
    } catch (e: any) {
      setErr(
        e?.response?.data?.error?.message ?? e?.message ?? 'Login failed — check your details',
      );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 to-slate-700 p-4">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full max-w-sm rounded-xl bg-white p-8 shadow-xl"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-lg bg-brand-600 p-2 text-white">
            <Users className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Guardian Login</h1>
            <p className="text-xs text-slate-500">See all your children, across schools</p>
          </div>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Username</span>
          <input
            {...register('username')}
            autoFocus
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
          {errors.username && (
            <span className="text-xs text-red-600">{errors.username.message}</span>
          )}
        </label>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium">Password</span>
          <input
            type="password"
            {...register('password')}
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
          {errors.password && (
            <span className="text-xs text-red-600">{errors.password.message}</span>
          )}
        </label>

        {err && (
          <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
        )}

        <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="mt-4 text-center text-xs text-slate-500">
          Not a guardian?{' '}
          <a href="/portal/login" className="text-brand-600 hover:underline">
            Student &amp; Parent PIN portal
          </a>
        </p>
      </form>
    </div>
  );
}
